import { useState, useEffect } from 'react';
import moment from 'moment';
import { Tooltip, Zoom } from '@mui/material';
import { IoClose as Close } from 'react-icons/io5';
import { MdGroups as TeamIcon, MdPerson as PersonIcon, MdPeople as UsersIcon, MdAdminPanelSettings as AdminIcon, MdAccessTime as TimeIcon, MdBarChart as StatsIcon } from 'react-icons/md';
import Modal from '@mui/material/Modal';
import CircularProgress from '@mui/material/CircularProgress';
import ProfileView from '@CommonViews/ProfileView';
import UserProjectTeamStatsRow from '@ManagementRepresentationalDisplay/UserProjectTeamStatsRow';
import UserInfoService from '@Helpers/URLHelpers/UserInfoService';
import UserAnalysisService from '@Helpers/URLHelpers/AnalyticsService/UserAnalysisService';
import UserProjectTeamStatsService from '@Helpers/URLHelpers/AnalyticsService/UserProjectTeamStatsService';
import TeamService from '@Helpers/URLHelpers/TeamService';
import ErrorHandler from '@Root/ErrorHandler';
import './TeamInfoView.scss';

export default function TeamInfoView(props) {

    const [teamUsers, setTeamUsers] = useState([]);
    const [ownerUser, setOwnerUser] = useState({});
    const [showProfileView, setShowProfileView] = useState(false);
    const [profileId, setProfileId] = useState(0);
    const [teamAnalysis, setTeamAnalysis] = useState();
    const [userProjectTeamAnalysis, setUserProjectTeamAnalysis] = useState([]);
    const [openCount, setOpenCount] = useState(0);
    const [closedCount, setClosedCount] = useState(0);
    const [overdueCount, setOverdueCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const userInfoService = new UserInfoService();
    const userAnalysisService = new UserAnalysisService();
    const userProjectTeamStatsService = new UserProjectTeamStatsService();
    const teamService = new TeamService();
    const errorHandler = new ErrorHandler();
    const className = 'teamInfoView';

    useEffect(() => {
        if(!!props.team) renderData();
    }, [props]);

    const renderData = async() => {
        setLoading(true);
        try {
            await renderUserList();
            await renderTeamAnalysis();
            await renderUserProjectTeamAnalysis();
        } catch (err) {
            errorHandler.handleError(err);
        } finally {
            setLoading(false);
        }
    }

    const renderUserList = async() => {
        try {
            let userList = await teamService.getUserListByTeamId(props?.team?.id);
            userList = JSON.parse(userList.body);
            setTeamUsers(userList);
            let ownerUserObj = userList?.find((user) => user.id === props?.team?.ownerUserId);
            setOwnerUser(ownerUserObj);
        } catch (err) {
            errorHandler.handleError(err);
        }
    }

    const renderTeamAnalysis = async() => {
        try {
            let body = {
                teamId: props?.team?.id,
                projectIdList: props?.projectIdList
            }
            let teamAnalysisObj = await userAnalysisService.getUserAnalysisByTeamAndProjectIdList(body);
            teamAnalysisObj = JSON.parse(teamAnalysisObj.body);
            setTeamAnalysis(teamAnalysisObj);
            let openCountValue = teamAnalysisObj?.open || 0;
            setOpenCount(openCountValue);
            let totalCountValue = teamAnalysisObj?.total || 0;
            setClosedCount(totalCountValue - openCountValue);
            let overdueCountValue = teamAnalysisObj?.overdue || 0;
            setOverdueCount(overdueCountValue);
        } catch (err) {
            errorHandler.handleError(err);
        }
    }

    const renderUserProjectTeamAnalysis = async() => {
        try {
            let userProjectTeamAnalysisObj = await userProjectTeamStatsService.getListByProjectAndTeam(JSON.parse(props?.projectIdList)[0], props?.team?.id);
            userProjectTeamAnalysisObj = JSON.parse(userProjectTeamAnalysisObj.body);
            setUserProjectTeamAnalysis(userProjectTeamAnalysisObj);
        } catch (err) {
            errorHandler.handleError(err);
        }
    }

    const toggleProfileView = () => {
        setShowProfileView(!showProfileView)
    }

    const handleUserClick = (userId) => {
        setProfileId(userId);
        setShowProfileView(!showProfileView);
    }

    const formatDate = (timestamp) => {
        return new Date(timestamp).toLocaleString('en-US', { 
            month: 'short', 
            day: '2-digit', 
            year: 'numeric',
            hour: '2-digit', 
            minute: '2-digit', 
            hour12: true 
        });
    }

    return (
        <Modal
            open={props.open}
            onClose={props.closePopup}>
            <div className={className}>
                <div className={`${className}__container`}>
                    {/* Header */}
                    <div className={`${className}__header`}>
                        <div className={`${className}__header__left`}>
                            <div className={`${className}__header__icon`}>
                                <TeamIcon />
                            </div>
                            <div className={`${className}__header__info`}>
                                <div className={`${className}__header__titleRow`}>
                                    <span className={`${className}__header__tag`}>{props?.team?.tag}</span>
                                    <h2 className={`${className}__header__title`}>{props?.team?.teamName}</h2>
                                </div>
                                <p className={`${className}__header__subtitle`}>Team Information</p>
                            </div>
                        </div>
                        <button 
                            className={`${className}__header__close`} 
                            onClick={props.closePopup}>
                            <Close />
                        </button>
                    </div>

                    {loading ? (
                        <div className={`${className}__loading`}>
                            <CircularProgress size={40} />
                            <p>Loading team information...</p>
                        </div>
                    ) : (
                        <div className={`${className}__content`}>
                            {/* Info Cards */}
                            <div className={`${className}__infoGrid`}>
                                {/* Created Date */}
                                <div className={`${className}__infoCard`}>
                                    <TimeIcon className={`${className}__infoCard__icon`} />
                                    <div className={`${className}__infoCard__content`}>
                                        <div className={`${className}__infoCard__label`}>Created</div>
                                        <div className={`${className}__infoCard__value`}>
                                            {formatDate(props?.team?.createTimeStamp)}
                                        </div>
                                    </div>
                                </div>

                                {/* Team Owner */}
                                <div 
                                    className={`${className}__infoCard clickable`}
                                    onClick={() => handleUserClick(ownerUser?.id)}>
                                    <AdminIcon className={`${className}__infoCard__icon`} />
                                    <div className={`${className}__infoCard__content`}>
                                        <div className={`${className}__infoCard__label`}>Team Owner</div>
                                        <div className={`${className}__infoCard__value`}>
                                            {ownerUser?.fullName}
                                            <span className={`${className}__infoCard__username`}>
                                                @{ownerUser?.userName}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Team Size */}
                                <div className={`${className}__infoCard`}>
                                    <UsersIcon className={`${className}__infoCard__icon`} />
                                    <div className={`${className}__infoCard__content`}>
                                        <div className={`${className}__infoCard__label`}>Team Members</div>
                                        <div className={`${className}__infoCard__value`}>
                                            {teamUsers?.length || 0} {teamUsers?.length === 1 ? 'Member' : 'Members'}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Status Overview */}
                            <div className={`${className}__section`}>
                                <div className={`${className}__section__header`}>
                                    <StatsIcon />
                                    <h3>Status Overview</h3>
                                </div>
                                <div className={`${className}__statusGrid`}>
                                    <div className={`${className}__statusCard open`}>
                                        <div className={`${className}__statusCard__label`}>Open</div>
                                        <div className={`${className}__statusCard__value`}>{openCount}</div>
                                    </div>
                                    <div className={`${className}__statusCard closed`}>
                                        <div className={`${className}__statusCard__label`}>Closed</div>
                                        <div className={`${className}__statusCard__value`}>{closedCount}</div>
                                    </div>
                                    <div className={`${className}__statusCard overdue`}>
                                        <div className={`${className}__statusCard__label`}>Overdue</div>
                                        <div className={`${className}__statusCard__value`}>{overdueCount}</div>
                                    </div>
                                </div>
                            </div>

                            {/* User Statistics */}
                            {userProjectTeamAnalysis?.length > 0 && (
                                <div className={`${className}__section`}>
                                    <div className={`${className}__section__header`}>
                                        <PersonIcon />
                                        <h3>Member Statistics</h3>
                                    </div>
                                    <div className={`${className}__statsList`}>
                                        {userProjectTeamAnalysis.map((userAnalysis, index) => (
                                            <UserProjectTeamStatsRow
                                                key={index}
                                                userAnalysis={userAnalysis}
                                                openCount={openCount}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {showProfileView && (
                    <ProfileView
                        open={showProfileView}
                        closePopup={toggleProfileView}
                        profileId={profileId}
                    />
                )}
            </div>
        </Modal>
    );
}
